import { useCallback, useEffect, useState } from "react";
import { ImagePlus, Share2 } from "lucide-react";
import { toast } from "sonner";
import { Marigold, Sparkle, SunDiya } from "@/components/art/Poster";
import { useCourtyard } from "@/context/CourtyardContext";
import { http, photoSrc } from "@/lib/api";
import { copyText, inviteLink } from "@/lib/event";
import { Reveal } from "@/components/invite/Reveal";
import UploadDialog from "@/components/invite/UploadDialog";
import Lightbox from "@/components/invite/Lightbox";

const TILT = [-2.5, 1.8, -1.2, 2.4, -0.8, 1.2];
const TITLE = "Drama ke Saboot";
const LINE = "Photos idhar. Context group chat mein.";

function EmptyState() {
  return (
    <div className="mx-auto flex max-w-md flex-col items-center py-8 text-center" data-testid="gallery-empty">
      <div className="w-40"><SunDiya lit /></div>
      <p className="font-serif-i mt-6 text-3xl leading-tight">The lights are ready. The memories are on their way.</p>
    </div>
  );
}

export default function PhotoWall() {
  const { event: ev } = useCourtyard();
  const [photos, setPhotos] = useState([]);
  const [loadError, setLoadError] = useState(false);
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(-1);

  const load = useCallback(() => {
    http
      .get("/photos")
      .then((r) => {
        setPhotos(Array.isArray(r.data) ? r.data : []);
        setLoadError(false);
      })
      .catch(() => setLoadError(true));
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const share = async () => {
    const url = `${inviteLink(ev)}/#memories`;
    try {
      if (navigator.share) await navigator.share({ title: TITLE, text: `${LINE} ${ev.title} ke saboot yahan daalo:`, url });
      else if (await copyText(url)) toast.success("Photo wall link copied");
    } catch (e) {
      if (e?.name !== "AbortError" && (await copyText(url))) toast.success("Photo wall link copied");
    }
  };

  return (
    <section id="memories" className="scroll-mt-16" aria-labelledby="memories-title" data-testid="photo-wall-gallery">
      <div className="bg-pink-print on-dark relative overflow-hidden border-y-[2.5px] border-[var(--navy)] py-16 lg:py-24">
        <div className="dots-cream pointer-events-none absolute inset-0" aria-hidden="true" />
        <Marigold className="absolute right-[6%] top-6 w-14 sm:w-20" />
        <Sparkle className="absolute bottom-8 right-[30%] w-8" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
          <Reveal>
            <p className="eyebrow eyebrow-light">Yaadein</p>
            <h2 id="memories-title" className="font-poster mt-2 text-[clamp(3.6rem,17vw,10rem)] leading-[0.86] text-[var(--cream)]">
              Drama ke <span className="font-serif-i text-[1.1em] text-[var(--yellow)]">Saboot</span>
            </h2>
            <p className="font-serif-i mt-4 text-2xl text-[var(--cream)] sm:text-3xl">{LINE}</p>
            <div className="mt-8 flex flex-col gap-4 sm:flex-row">
              {ev.photo_uploads_enabled ? (
                <button type="button" className="btn-primary text-lg" onClick={() => setOpen(true)} data-testid="photo-upload-button">
                  <ImagePlus className="h-5 w-5" aria-hidden="true" /> Add a memory
                </button>
              ) : (
                <p className="pill self-start" data-testid="uploads-paused">Photo uploads are paused by {ev.host_names}</p>
              )}
              <button type="button" className="btn-ghost text-lg" onClick={share} data-testid="photo-share-button">
                <Share2 className="h-5 w-5" aria-hidden="true" /> Share the wall
              </button>
            </div>
          </Reveal>
        </div>
      </div>
      <div className="paper relative py-14 lg:py-20">
        <div className="dots-navy pointer-events-none absolute inset-0 opacity-50" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
          {loadError && <p>We couldn’t load the photos just now. Please refresh in a moment.</p>}
          {!loadError && photos.length === 0 && <EmptyState />}
          {photos.length > 0 && (
            <ul className="columns-2 gap-5 sm:columns-3 lg:columns-4 lg:gap-7" data-testid="gallery-grid">
              {photos.map((p, i) => (
                <Reveal as="li" key={p.id} delay={(i % 4) * 0.05} className="mb-6 break-inside-avoid px-1">
                  <figure className="border-2 border-[var(--navy)] bg-white p-2.5 pb-3 shadow-[4px_4px_0_#142344] transition-transform duration-300 hover:rotate-0 hover:scale-[1.02]" style={{ transform: `rotate(${TILT[i % TILT.length]}deg)` }}>
                    <button type="button" onClick={() => setIndex(i)} className="block w-full overflow-hidden" aria-label={`Open photo${p.caption ? `: ${p.caption}` : ""}`} data-testid={`gallery-photo-${i}`}>
                      <img src={photoSrc(p.id)} alt={p.caption || "A party memory"} width={p.width} height={p.height} loading="lazy" decoding="async" className="h-auto w-full" />
                    </button>
                    <figcaption className="px-1 pt-2.5 text-center">
                      <span className="font-serif-i block text-xl leading-tight">{p.caption || "Saboot #" + (photos.length - i)}</span>
                      {p.display_name && <span className="block text-xs font-bold text-[var(--muted-ink)]">by {p.display_name}</span>}
                    </figcaption>
                  </figure>
                </Reveal>
              ))}
            </ul>
          )}
        </div>
      </div>
      <UploadDialog open={open} onOpenChange={setOpen} onUploaded={load} />
      <Lightbox photos={photos} index={index} setIndex={setIndex} />
    </section>
  );
}
